/**
 * Casca da aplicacao: escolha de perfil, lobby e partida.
 *
 * Tudo aqui roda em modo demonstracao, com saldo virtual. Nenhuma tela desta
 * versao movimenta dinheiro real — deposito e saque exigem o backend, KYC e o
 * enquadramento legal resolvido.
 */
import { useEffect, useState } from 'react';

import { PresencaJogoBadge, PresencaTotal, usePresenca } from './components/Presenca.tsx';
import { Salvaguarda } from './components/Salvaguarda.tsx';
import { type GameType, createDemoMatch } from './lib/adapter.ts';
import { DEMO_PROFILES, type DemoProfile, STAKES, payoutCents, rakeCents } from './lib/demo.ts';
import { GAME_LABEL, money } from './lib/format.ts';
import { carregarConta } from './lib/carteira.ts';
import type { EstadoAposta } from './lib/conta.ts';
import { createLiveMatch } from './lib/liveAdapter.ts';
import { sair as encerrarSessao, supabaseConfigurado } from './lib/sessao.ts';
import { usarSessao } from './lib/usarSessao.ts';
import { Avatar } from './components/Avatar.tsx';
import { MesaCard } from './components/MesaCard.tsx';
import { Marca } from './components/Marca.tsx';
import { type PerfilPublico, carregarPerfil } from './lib/perfil.ts';
import { AcertarValor } from './screens/AcertarValor.tsx';
import { Admin } from './screens/Admin.tsx';
import { Carteira } from './screens/Carteira.tsx';
import { Entrar } from './screens/Entrar.tsx';
import { LobbyReal } from './screens/LobbyReal.tsx';
import { PortaoIdentidade } from './screens/Identidade.tsx';
import { Perfil as PerfilDoJogador } from './screens/Perfil.tsx';
import { Salao } from './screens/Salao.tsx';
import { type MatchResult, MatchScreen } from './screens/Match.tsx';

type Tela =
  | { readonly nome: 'perfis' }
  | { readonly nome: 'entrar' }
  | { readonly nome: 'lobby' }
  | { readonly nome: 'partida'; readonly gameType: GameType; readonly entryFeeCents: number; readonly seed: number }
  | { readonly nome: 'carteira' }
  | { readonly nome: 'perfil' }
  | { readonly nome: 'salao' }
  | {
      readonly nome: 'acertar-valor';
      readonly matchId: string;
      readonly gameType: GameType;
      readonly aposta: EstadoAposta | null;
    }
  | {
      readonly nome: 'partida-real';
      readonly matchId: string;
      readonly gameType: GameType;
      readonly entryFeeCents: number;
      /** Levado da negociação para o tabuleiro: o desfecho mostra o rosto. */
      readonly adversario?: PerfilPublico | null;
    };

const JOGOS: readonly GameType[] = ['TIC_TAC_TOE', 'CHECKERS', 'CHESS'];

/** Nome do adversario de demonstracao, por perfil. */
const OPONENTES: Record<string, string> = {
  iniciante: 'Léo',
  damista: 'Dona Vera',
  enxadrista: 'Ana',
};

const DESCRICAO_JOGO: Record<GameType, string> = {
  TIC_TAC_TOE: 'Rápido. Bom para conhecer a mesa.',
  CHECKERS: 'Damas brasileiras: captura obrigatória e lei da maioria.',
  CHESS: 'Regras completas, relógio com incremento.',
};

// ---------------------------------------------------------------------
/**
 * Caminho atual da URL.
 *
 * A aplicação é uma máquina de estados, não um roteador — e continua
 * sendo. O painel administrativo é a única coisa que precisa de endereço
 * próprio: é para onde alguém vai direto, de madrugada, sem passar pelo
 * lobby. Um roteador inteiro para uma rota seria peso sem uso.
 */
/**
 * Saldo real da conta, para o cabeçalho.
 *
 * Recarrega quando a tela muda, que é quando ele pode ter mudado: depois
 * de um depósito, de uma partida liquidada, de escrow travado. O cabeçalho
 * mostrar zero enquanto a carteira mostra vinte, na mesma tela, é pior que
 * não mostrar saldo nenhum — quem lê não sabe em qual acreditar.
 */
function useSaldoReal(
  token: string | null,
  dependencia: string,
): {
  disponivel: number;
  escrow: number;
  /**
   * `null` enquanto a resposta não chegou. Distinguir de `false` importa:
   * tratar "ainda não sei" como "não declarou" faria o portão piscar na
   * cara de quem já tinha declarado, a cada troca de tela.
   */
  identidadeDeclarada: boolean | null;
  recarregar: () => void;
} {
  const [saldo, setSaldo] = useState<{
    disponivel: number;
    escrow: number;
    identidadeDeclarada: boolean | null;
  }>({ disponivel: 0, escrow: 0, identidadeDeclarada: null });
  const [gatilho, setGatilho] = useState(0);

  useEffect(() => {
    if (!token) return;
    let vivo = true;
    void carregarConta(token).then((resultado) => {
      if (!vivo || resultado.estado !== 'ok') return;
      setSaldo({
        disponivel: resultado.dado.balance.availableCents,
        escrow: resultado.dado.balance.escrowCents,
        identidadeDeclarada: resultado.dado.identityDeclared,
      });
    });
    return () => {
      vivo = false;
    };
  }, [token, dependencia, gatilho]);

  return { ...saldo, recarregar: () => setGatilho((n) => n + 1) };
}

/**
 * Nome e avatar de quem está logado, para o cabeçalho.
 *
 * Recarrega quando a tela muda pelo mesmo motivo do saldo: sair da tela de
 * perfil com um avatar novo e continuar vendo o antigo no cabeçalho faria
 * a pessoa duvidar se salvou.
 */
function useMeuPerfil(token: string | null, dependencia: string): PerfilPublico | null {
  const [perfil, setPerfil] = useState<PerfilPublico | null>(null);

  useEffect(() => {
    if (!token) return;
    let vivo = true;
    void carregarPerfil(token).then((resultado) => {
      if (vivo && resultado.estado === 'ok') setPerfil(resultado.dado.perfil);
    });
    return () => {
      vivo = false;
    };
  }, [token, dependencia]);

  return perfil;
}

function useCaminho(): [string, (destino: string) => void] {
  const [caminho, setCaminho] = useState(() => globalThis.location?.pathname ?? '/');

  useEffect(() => {
    const aoVoltar = (): void => setCaminho(globalThis.location.pathname);
    globalThis.addEventListener('popstate', aoVoltar);
    return () => globalThis.removeEventListener('popstate', aoVoltar);
  }, []);

  return [
    caminho,
    (destino) => {
      globalThis.history.pushState({}, '', destino);
      setCaminho(destino);
    },
  ];
}

function useTema(): [boolean, () => void] {
  const [escuro, setEscuro] = useState(
    () => globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false,
  );

  useEffect(() => {
    const raiz = document.documentElement;
    raiz.classList.toggle('dark', escuro);
    raiz.classList.toggle('light', !escuro);
  }, [escuro]);

  return [escuro, () => setEscuro((v) => !v)];
}

/** Icone em SVG: os glifos ☀/☾ nao existem em toda fonte e viram asterisco. */
function IconeTema({ escuro }: { escuro: boolean }) {
  return escuro ? (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </svg>
  );
}

function Cabecalho({
  perfil,
  saldoCents,
  emEscrowCents,
  onSair,
  logado,
  rotuloSair,
  onCarteira,
  meuPerfil,
  onPerfil,
}: {
  perfil: DemoProfile | null;
  saldoCents: number;
  emEscrowCents: number;
  onSair: () => void;
  logado?: boolean;
  rotuloSair?: string;
  /** Ausente esconde o botão: é assim que a própria carteira o omite. */
  onCarteira?: () => void;
  /** Nome e avatar de quem está logado, para o cabeçalho ter rosto. */
  meuPerfil?: PerfilPublico | null;
  onPerfil?: () => void;
}) {
  const [escuro, alternar] = useTema();

  return (
    <header
      className="sticky top-0 z-10 border-b backdrop-blur-md"
      style={{
        background: 'color-mix(in oklab, var(--surface) 82%, transparent)',
        borderBottomColor: 'color-mix(in oklab, var(--brass) 35%, var(--border))',
      }}
    >
      <div className="mx-auto max-w-5xl px-4 h-14 flex items-center gap-2 sm:gap-4">
        <Marca />

        <div className="ml-auto flex items-center gap-2 sm:gap-3 min-w-0">
          {perfil || logado ? (
            <>
              <div className="text-right leading-tight min-w-0">
                <div className="rotulo truncate">saldo</div>
                <div className="money truncate">{money(saldoCents)}</div>
              </div>
              {emEscrowCents > 0 ? (
                <div className="hidden sm:block text-right leading-tight min-w-0">
                  <div className="rotulo truncate">em disputa</div>
                  <div className="money truncate">{money(emEscrowCents)}</div>
                </div>
              ) : null}
            </>
          ) : null}

          {onCarteira ? (
            <button
              type="button"
              onClick={onCarteira}
              className="btn btn-ghost !px-3 !py-1.5 shrink-0 whitespace-nowrap"
            >
              Carteira
            </button>
          ) : null}

          {/* O avatar é o caminho para o perfil. Um botão escrito
              "Perfil" ao lado de "Carteira" competiria por atenção com
              ele sem acrescentar nada: o rosto já diz de quem é. */}
          {onPerfil ? (
            <button
              type="button"
              onClick={onPerfil}
              className="shrink-0 rounded-full transition"
              aria-label="Abrir seu perfil"
              title={meuPerfil?.displayName ?? 'Seu perfil'}
            >
              <Avatar
                avatar={meuPerfil?.avatar}
                cor={meuPerfil?.avatarColor}
                tamanho={30}
                nome={meuPerfil?.displayName ?? 'você'}
              />
            </button>
          ) : null}

          <button
            type="button"
            onClick={alternar}
            className="btn btn-ghost !px-2.5 !py-1.5 shrink-0"
            aria-label={escuro ? 'Usar tema claro' : 'Usar tema escuro'}
          >
            <IconeTema escuro={escuro} />
          </button>

          {perfil || logado ? (
            <button
              type="button"
              className="btn btn-ghost !px-3 !py-1.5 shrink-0 whitespace-nowrap"
              onClick={onSair}
            >
              <span className="hidden sm:inline">{rotuloSair ?? 'Trocar perfil'}</span>
              <span className="sm:hidden" aria-hidden="true">
                Sair
              </span>
              <span className="sr-only sm:hidden">{rotuloSair ?? 'Trocar perfil'}</span>
            </button>
          ) : null}
        </div>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------
/**
 * Página de entrada.
 *
 * A demonstração deixou de ser o assunto. Ela existia como porta da frente
 * enquanto não havia contra quem jogar, e nesse período fazia sentido: uma
 * plataforma vazia que abre pedindo cadastro não dá a ninguém motivo para
 * se cadastrar.
 *
 * Agora inverte. Quem chega vê primeiro o que o produto é — partida por
 * dinheiro contra outra pessoa — e a demonstração fica como o que ela de
 * fato é: um jeito de experimentar as regras antes de decidir.
 *
 * O que não muda é a honestidade dos rótulos. A demonstração continua
 * dizendo em letra grande que é saldo virtual contra um adversário do
 * computador. Um botão "jogar agora" que leva a um bot sem avisar seria a
 * forma mais barata de inflar cadastro, e a mais cara de perder confiança
 * no primeiro saque que a pessoa tentasse.
 */
function Entrada({
  onEscolher,
  onEntrar,
}: {
  onEscolher: (perfil: DemoProfile) => void;
  onEntrar: (() => void) | null;
}) {
  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-12 grid gap-12 content-start grow">
      <section className="grid gap-8 md:grid-cols-[1.15fr_auto] md:items-center">
        <div className="max-w-xl">
          <h1 className="font-bold" style={{ fontSize: 'var(--text-display)', lineHeight: 1.05 }}>
            A habilidade decide.
            <br />
            <span style={{ color: 'var(--primary)' }}>A sorte não entra.</span>
          </h1>
          <p className="mt-4 text-muted-foreground" style={{ fontSize: 'var(--text-lead)' }}>
            Xadrez, damas e jogo da velha contra outras pessoas, valendo dinheiro de verdade.
            Vocês combinam quanto vale antes de começar, e o valor fica retido até a partida
            terminar.
          </p>

          {onEntrar ? (
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button type="button" className="btn btn-primary !py-3 !px-6" onClick={onEntrar}>
                Criar conta e jogar
              </button>
              <button type="button" className="btn btn-ghost !py-3" onClick={onEntrar}>
                Já tenho conta
              </button>
            </div>
          ) : null}

          <ul className="mt-7 grid gap-2 text-sm text-muted-foreground">
            <li>Não existe adversário da casa: toda partida é entre duas pessoas.</li>
            <li>Empate devolve a entrada inteira, sem comissão.</li>
            <li>O saque volta para o CPF da sua própria conta, e só para ele.</li>
          </ul>
        </div>

        {/* Miniatura de tabuleiro: abre com o assunto da pagina, nao com
            um bloco de texto. Decorativa, entao fica fora da arvore de
            acessibilidade. */}
        <div aria-hidden="true" className="hidden md:block moldura w-56 shrink-0 rotate-[-4deg]">
          <div className="aspect-square grid grid-cols-6 grid-rows-6 rounded-sm overflow-hidden">
            {Array.from({ length: 36 }, (_unused, i) => {
              const linha = Math.floor(i / 6);
              const coluna = i % 6;
              const escura = (linha + coluna) % 2 === 1;
              const pecaClara = escura && linha >= 4;
              const pecaEscura = escura && linha <= 1;
              return (
                <div
                  key={i}
                  className="grid place-items-center"
                  style={{ background: escura ? 'var(--board-dark)' : 'var(--board-light)' }}
                >
                  {pecaClara || pecaEscura ? (
                    <span
                      className="block w-[72%] aspect-square rounded-full"
                      style={{
                        background: pecaClara ? 'var(--piece-light)' : 'var(--piece-dark)',
                        boxShadow: '0 1px 2px oklch(0 0 0 / 0.4)',
                      }}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="grid gap-5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 className="font-bold" style={{ fontSize: 'var(--text-title)' }}>
            Experimentar sem conta
          </h2>
          <span className="selo">saldo virtual, adversário do computador</span>
        </div>
        <p className="text-muted-foreground max-w-2xl">
          Para conhecer as regras e o ritmo do relógio. Nada aqui envolve dinheiro, e nada do que
          acontecer nestas partidas conta no seu histórico.
        </p>

        <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {DEMO_PROFILES.map((perfil) => (
            <li key={perfil.id} className="card p-5 flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <span
                  className="grid place-items-center size-11 rounded-lg text-2xl leading-none shrink-0"
                  aria-hidden="true"
                  style={{
                    background: 'var(--board-dark)',
                    color: 'var(--board-light)',
                    boxShadow:
                      'inset 0 1px 2px oklch(1 0 0 / 0.15), 0 1px 2px oklch(0 0 0 / 0.25)',
                  }}
                >
                  {perfil.avatar}
                </span>
                <div className="min-w-0">
                  <h3 className="font-semibold truncate">{perfil.nome}</h3>
                  <p className="text-sm text-muted-foreground truncate">{perfil.apelido}</p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground">{perfil.descricao}</p>

              <button
                type="button"
                className="btn btn-ghost mt-auto"
                onClick={() => onEscolher(perfil)}
              >
                Experimentar como {perfil.apelido}
              </button>
            </li>
          ))}
        </ul>
      </section>

      <p className="text-center text-sm text-muted-foreground">
        Xadrez, damas e jogo da velha são jogos de habilidade: o resultado depende do desempenho de
        quem joga. Partidas com valor real exigem 18 anos e verificação de identidade.
      </p>
    </main>
  );
}

// ---------------------------------------------------------------------
function Lobby({
  perfil,
  saldoCents,
  onJogar,
}: {
  perfil: DemoProfile;
  saldoCents: number;
  onJogar: (gameType: GameType, entryFeeCents: number) => void;
}) {
  const [jogo, setJogo] = useState<GameType>(perfil.jogoFavorito);
  const [entrada, setEntrada] = useState<number>(STAKES[1]?.cents ?? 2000);
  // A demonstracao joga com saldo virtual, entao a presenca consultada e a
  // das mesas virtuais.
  const presenca = usePresenca('VIRTUAL');

  const semSaldo = entrada > saldoCents;

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-8 grid gap-6 lg:grid-cols-[1fr_20rem] items-start content-start grow">
      <section className="grid gap-4 content-start">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <h1 className="font-bold" style={{ fontSize: 'var(--text-title)' }}>
            Escolha a mesa
          </h1>
          <PresencaTotal presenca={presenca} />
        </div>

        <ul className="grid gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Jogo">
          {JOGOS.map((tipo) => (
            <li key={tipo}>
              <MesaCard
                gameType={tipo}
                titulo={GAME_LABEL[tipo] ?? tipo}
                descricao={DESCRICAO_JOGO[tipo] ?? ''}
                selecionado={jogo === tipo}
                onSelecionar={() => setJogo(tipo)}
                rodape={<PresencaJogoBadge presenca={presenca} gameType={tipo} />}
              />
            </li>
          ))}
        </ul>

        <h2 className="mt-2 rotulo">Valor da entrada</h2>
        <ul className="flex flex-wrap gap-2" role="radiogroup" aria-label="Valor da entrada">
          {STAKES.map((stake) => (
            <li key={stake.cents}>
              <button
                type="button"
                role="radio"
                aria-checked={entrada === stake.cents}
                onClick={() => setEntrada(stake.cents)}
                disabled={stake.cents > saldoCents}
                className={[
                  'btn',
                  entrada === stake.cents ? 'btn-primary' : 'btn-ghost',
                ].join(' ')}
              >
                {stake.rotulo}
              </button>
            </li>
          ))}
        </ul>
      </section>

      <aside className="card card-destaque p-5 grid gap-4 content-start">
        <h2 className="rotulo">Como fica o pote</h2>

        <dl className="grid gap-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Sua entrada</dt>
            <dd className="money">{money(entrada)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Pote</dt>
            <dd className="money">{money(entrada * 2)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Taxa da plataforma (5%)</dt>
            <dd className="money">−{money(rakeCents(entrada))}</dd>
          </div>
          <div className="flex justify-between gap-4 border-t pt-2 font-semibold">
            <dt>Prêmio ao vencedor</dt>
            <dd className="money">{money(payoutCents(entrada))}</dd>
          </div>
        </dl>

        <p className="text-xs text-muted-foreground">
          Empate devolve a entrada integral, sem taxa. A entrada fica retida até a partida terminar.
        </p>

        <button
          type="button"
          className="btn btn-primary"
          disabled={semSaldo}
          onClick={() => onJogar(jogo, entrada)}
        >
          {semSaldo ? 'Saldo insuficiente' : 'Procurar adversário'}
        </button>
      </aside>
    </main>
  );
}

// ---------------------------------------------------------------------
export default function App() {
  const sessao = usarSessao();
  const [caminho, irPara] = useCaminho();

  const [perfil, setPerfil] = useState<DemoProfile | null>(null);
  const [saldoCents, setSaldoCents] = useState(0);
  const [escrowCents, setEscrowCents] = useState(0);
  const [tela, setTela] = useState<Tela>({ nome: 'perfis' });

  // Chamado incondicionalmente: hook depois de retorno condicional muda de
  // ordem entre renders, e o React não permite.
  /**
   * Treino ou valendo, para a área logada inteira.
   *
   * Mora aqui, e não em cada tela, porque o salão e a fila precisam
   * concordar: abrir mesa num modo e listar no outro deixaria a pessoa
   * esperando numa sala em que ninguém a vê.
   *
   * Começa em treino de propósito. Quem acabou de criar conta ainda não
   * declarou identidade nem depositou, e abrir direto no modo que exige as
   * duas coisas transformaria a primeira tela numa recusa.
   */
  const [modoAposta, setModoAposta] = useState<'REAL' | 'VIRTUAL'>('VIRTUAL');

  const meuPerfil = useMeuPerfil(
    sessao.estado === 'logado' ? sessao.sessao.access_token : null,
    tela.nome,
  );
  const saldoReal = useSaldoReal(
    sessao.estado === 'logado' ? sessao.sessao.access_token : null,
    tela.nome,
  );

  // Ao entrar ou sair da conta, a tela volta para o começo do fluxo certo.
  useEffect(() => {
    if (sessao.estado === 'logado') {
      setPerfil(null);
      setTela({ nome: 'lobby' });
    } else if (sessao.estado === 'deslogado') {
      setTela((atual) => (atual.nome === 'entrar' ? atual : { nome: 'perfis' }));
    }
  }, [sessao.estado]);

  function escolherPerfil(escolhido: DemoProfile): void {
    setPerfil(escolhido);
    setSaldoCents(escolhido.saldoVirtualCents);
    setEscrowCents(0);
    setTela({ nome: 'lobby' });
  }

  function trocarPerfil(): void {
    setPerfil(null);
    setEscrowCents(0);
    setTela({ nome: 'perfis' });
  }

  function jogarDemo(gameType: GameType, entryFeeCents: number): void {
    // Mesma mecanica do escrow do servidor: a entrada sai do saldo e fica
    // retida enquanto a partida corre.
    setSaldoCents((atual) => atual - entryFeeCents);
    setEscrowCents(entryFeeCents);
    setTela({ nome: 'partida', gameType, entryFeeCents, seed: Date.now() % 2_147_483_647 });
  }

  function liquidarDemo(resultado: MatchResult): void {
    setEscrowCents(0);
    setSaldoCents((atual) => atual + resultado.creditoCents);
  }

  // -------------------------------------------------------------------
  // Conta
  // -------------------------------------------------------------------
  if (sessao.estado === 'carregando') {
    return (
      <div className="min-h-dvh grid place-items-center text-muted-foreground">
        Abrindo a mesa…
      </div>
    );
  }

  if (sessao.estado === 'logado') {
    const token = sessao.sessao.access_token;
    const userId = sessao.sessao.user.id;

    // Quem decide se esta pessoa é administradora é o banco: aqui só se
    // pergunta. Esconder a rota no cliente não protegeria nada.
    if (caminho === '/admin') {
      return (
        <div className="min-h-dvh flex flex-col">
          <Cabecalho
            perfil={null}
            saldoCents={saldoReal.disponivel}
            emEscrowCents={saldoReal.escrow}
            logado
            rotuloSair="Sair da conta"
            onSair={() => void encerrarSessao()}
          />
          <Admin token={token} onSair={() => irPara('/')} />
        </div>
      );
    }

    /**
     * Para onde ir quando uma mesa se forma.
     *
     * Valor já fechado significa que a negociação acabou enquanto esta
     * tela não olhava — recarregar a página no meio do jogo, por exemplo.
     * Aí o caminho é o tabuleiro, e não a mesa de negociação de um acordo
     * que já existe.
     *
     * Sai numa função só porque duas telas levam à partida — o salão e a
     * fila — e as duas precisam concordar sobre isso.
     */
    const irParaPartida = (
      matchId: string,
      gameType: GameType,
      aposta: EstadoAposta | null,
    ): void =>
      setTela(
        aposta?.stakeAgreed
          ? {
              nome: 'partida-real',
              matchId,
              gameType,
              entryFeeCents: aposta.entryFeeCents,
              adversario: aposta.opponent,
            }
          : { nome: 'acertar-valor', matchId, gameType, aposta },
      );

    // O portão vem antes de tudo. Quem entrou pelo Google não passou por
    // formulário nenhum, e contas criadas antes de o cadastro pedir CPF
    // também chegam aqui sem declarar. Deixar circular e recusar mais
    // adiante seria descobrir a exigência no pior momento: quando a
    // pessoa já quis jogar valendo.
    if (saldoReal.identidadeDeclarada === false) {
      return (
        <div className="min-h-dvh flex flex-col">
          <Cabecalho
            perfil={null}
            saldoCents={0}
            emEscrowCents={0}
            logado
            rotuloSair="Sair da conta"
            onSair={() => void encerrarSessao()}
          />
          <PortaoIdentidade
            token={token}
            onPronto={() => saldoReal.recarregar()}
            onSair={() => void encerrarSessao()}
          />
        </div>
      );
    }

    return (
      <div className="min-h-dvh flex flex-col">
        <Cabecalho
          perfil={null}
          saldoCents={saldoReal.disponivel}
          emEscrowCents={saldoReal.escrow}
          logado
          rotuloSair="Sair da conta"
          onSair={() => void encerrarSessao()}
          onCarteira={tela.nome === 'carteira' ? undefined : () => setTela({ nome: 'carteira' })}
          meuPerfil={meuPerfil}
          onPerfil={tela.nome === 'perfil' ? undefined : () => setTela({ nome: 'perfil' })}
        />

        {/* Tela branca é a pior mensagem possível num produto com dinheiro
            travado em escrow: não diz se o saldo se perdeu, nem o que
            fazer. A salvaguarda troca isso por um caminho de volta e pelo
            erro à vista. */}
        <Salvaguarda onVoltar={() => setTela({ nome: 'salao' })}>
        {tela.nome === 'partida-real' ? (
          <MatchScreen
            chave={tela.matchId}
            gameType={tela.gameType}
            entryFeeCents={tela.entryFeeCents}
            adversario={tela.adversario ?? null}
            criarAdapter={() =>
              createLiveMatch({
                matchId: tela.matchId,
                token,
                gameType: tela.gameType,
                opponentName: tela.adversario?.displayName ?? 'Adversário',
              })
            }
            // Quem liquida é o servidor: aqui só se volta para o lobby, e o
            // saldo é relido do banco.
            onFinish={() => {}}
            onJogarDeNovo={() => setTela({ nome: 'salao' })}
            onLeave={() => setTela({ nome: 'salao' })}
          />
        ) : tela.nome === 'carteira' ? (
          <Carteira token={token} onVoltar={() => setTela({ nome: 'salao' })} />
        ) : tela.nome === 'perfil' ? (
          <PerfilDoJogador token={token} onVoltar={() => setTela({ nome: 'salao' })} />
        ) : tela.nome === 'acertar-valor' ? (
          // Entre parear e jogar existe um passo em que a partida já tem
          // dois jogadores e ainda não vale nada. O tabuleiro só abre
          // depois que o valor está travado dos dois lados.
          <AcertarValor
            token={token}
            matchId={tela.matchId}
            gameType={tela.gameType}
            apostaInicial={tela.aposta}
            onAcordo={(entryFeeCents) =>
              setTela({
                nome: 'partida-real',
                matchId: tela.matchId,
                gameType: tela.gameType,
                entryFeeCents,
                adversario: tela.aposta?.opponent ?? null,
              })
            }
            onDesfeita={() => setTela({ nome: 'salao' })}
          />
        ) : tela.nome === 'lobby' ? (
          <LobbyReal
            userId={userId}
            token={token}
            stakeMode={modoAposta}
            saldoRealCents={saldoReal.disponivel}
            onPartida={irParaPartida}
          />
        ) : (
          // O salão é a tela de entrada de quem está logado. A fila
          // continua existindo por trás dela: "abrir minha mesa" é entrar
          // nela e esperar, e é de lá que sai a mesa que os outros veem.
          <Salao
            token={token}
            stakeMode={modoAposta}
            onTrocarModo={setModoAposta}
            saldoRealCents={saldoReal.disponivel}
            onAbrirMesa={() => setTela({ nome: 'lobby' })}
            onCarteira={() => setTela({ nome: 'carteira' })}
            onPartida={irParaPartida}
          />
        )}
        </Salvaguarda>
      </div>
    );
  }

  // -------------------------------------------------------------------
  // Sem conta: demonstração, com a porta de entrada à vista
  // -------------------------------------------------------------------
  if (tela.nome === 'entrar' || caminho === '/admin') {
    return (
      <div className="min-h-dvh flex flex-col">
        <Cabecalho perfil={null} saldoCents={0} emEscrowCents={0} onSair={trocarPerfil} />
        <Entrar
          onVoltar={() => {
            if (caminho === '/admin') irPara('/');
            setTela({ nome: 'perfis' });
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex flex-col">
      <Cabecalho
        perfil={perfil}
        saldoCents={saldoCents}
        emEscrowCents={escrowCents}
        onSair={trocarPerfil}
      />

      {tela.nome === 'perfis' || !perfil ? (
        <Entrada
          onEscolher={escolherPerfil}
          onEntrar={supabaseConfigurado() ? () => setTela({ nome: 'entrar' }) : null}
        />
      ) : tela.nome === 'lobby' ? (
        <Lobby perfil={perfil} saldoCents={saldoCents} onJogar={jogarDemo} />
      ) : tela.nome === 'partida' ? (
        <MatchScreen
          chave={`${tela.gameType}:${tela.seed}`}
          gameType={tela.gameType}
          entryFeeCents={tela.entryFeeCents}
          criarAdapter={() =>
            createDemoMatch({
              gameType: tela.gameType,
              opponentName: OPONENTES[perfil.id] ?? 'Adversário',
              seed: tela.seed,
              skill: perfil.skillAdversario,
              initialMs: tela.gameType === 'TIC_TAC_TOE' ? 60_000 : 300_000,
              incrementMs: tela.gameType === 'TIC_TAC_TOE' ? 1_000 : 3_000,
            })
          }
          onFinish={liquidarDemo}
          onJogarDeNovo={() => setTela({ nome: 'lobby' })}
          onLeave={() => setTela({ nome: 'lobby' })}
        />
      ) : null}
    </div>
  );
}
